import React, { useContext, useEffect, useMemo, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormMultiSelectField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  BonusCampaignAvailabilityPeriod,
  BonusCampaignCountdownTrigger,
  BonusCampaignExcludeType,
  BonusLabel,
} from 'api/Wallet/Types/BonusCampaignTypes'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchWalletCurrenciesByCompanyAction } from 'store/wallet/wallet-actions'
import { selectCompanyWalletCurrencies } from 'store/wallet/wallet.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

import { NameDto } from '../../../api/Common/Types'
import { TickmillCompanyDto } from '../../../api/Identity/Types/Company'
import {
  AddCampaignDetailsFormValues,
  BonusCampaignStatus,
} from '../../../api/Wallet/Types/MarketingBonusCampaignDto'
import { WalletCurrencyDto } from '../../../api/Wallet/Types/WalletCurrencyDto'
import { AuthSessionContext } from '../../../utils/AuthContext'
import { UrlPattern } from '../../../utils/formValidation'
import { getUniqueCurrencies } from './BonusCampaignDetailsPage'

const AddBonusCampaignDetailsFormUI: React.FC<
  FormikProps<AddCampaignDetailsFormValues> & OuterProps
> = (props) => {
  const {
    values,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    tickmillCompanies,
    bonusTypes,
    errors,
    bonusLabels,
    bonusCampaignExcludeTypes,
    bonusCampaignAvialabilityPeriods,
    bonusCampaignCountdownTriggers,
  } = props

  const [walletCurrencies, setWalletCurrencies] = useState<WalletCurrencyDto[]>([])
  const [ibInputText, setIbInputText] = useState<string>('')
  const [affiliateInputText, setAffiliateInputText] = useState<string>('')
  const [utmInputText, setUtmInputText] = useState<string>('')
  const walletsApiClient = useApiClient(WalletsApiClient)

  const dispatch = useAppDispatch()
  const companyWalletCurrencies = useAppSelector(selectCompanyWalletCurrencies)

  const [auth] = useContext(AuthSessionContext)
  const dateFormatType = auth?.user.dateFormatType.name

  const handleSubmitForm = () => {
    handleSubmit()
  }

  useEffect(() => {
    if (companyWalletCurrencies) {
      dispatch(fetchWalletCurrenciesByCompanyAction(walletsApiClient, tickmillCompanies))
    }
  }, [])

  const handleSetCompany = (selected: number[]) => {
    setFieldValue('tickmillCompanyIds', selected)
    getTickmillCompanyCurrency(selected)
  }
  const getTickmillCompanyCurrency = async (tickmillCompanyIds: number[]) => {
    const uniqueCurrencies = await getUniqueCurrencies(tickmillCompanyIds, companyWalletCurrencies)

    setWalletCurrencies(uniqueCurrencies)
  }

  function handleOnSetIBs(text: string): void {
    setIbInputText(text)
  }

  function handleOnSetAffiliates(text: string): void {
    setAffiliateInputText(text)
  }

  function handleOnSetUtms(text: string): void {
    setUtmInputText(text)
  }

  function processIBsOnBlur(): void {
    const ibArray = ibInputText
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0)

    setFieldValue('bonusCampaignIntroducingBrokers', ibArray)
  }

  function processAffiliatesOnBlur(): void {
    const affiliateArray = affiliateInputText
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0)

    setFieldValue('bonusCampaignAffiliates', affiliateArray)
  }

  function processUtmsOnBlur(): void {
    const utmArray = utmInputText
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0)

    setFieldValue('bonusCampaignUtms', utmArray)
  }

  const getIBDisplayValue = () => {
    return (
      ibInputText ||
      (Array.isArray(values.bonusCampaignIntroducingBrokers)
        ? values.bonusCampaignIntroducingBrokers.join(', ')
        : '')
    )
  }

  const getAffiliateDisplayValue = () => {
    return (
      affiliateInputText ||
      (Array.isArray(values.bonusCampaignAffiliates)
        ? values.bonusCampaignAffiliates.join(', ')
        : '')
    )
  }

  const getUtmDisplayValue = () => {
    return (
      utmInputText ||
      (Array.isArray(values.bonusCampaignUtms) ? values.bonusCampaignUtms.join(', ') : '')
    )
  }

  const isBonusCampaignAvailabilityPeriodIdSelected = useMemo(() => {
    return (
      values.bonusCampaignAvailabilityPeriodId !== null &&
      values.bonusCampaignAvailabilityPeriodId > 0
    )
  }, [values.bonusCampaignAvailabilityPeriodId])

  useEffect(() => {
    if (!isBonusCampaignAvailabilityPeriodIdSelected) {
      setFieldValue('value', '')
      setFieldValue('bonusCampaignCountdownTriggerId', null)
    }
  }, [isBonusCampaignAvailabilityPeriodIdSelected])

  const isBonusCampaignExcludeTypeIdSelected = useMemo(() => {
    return values.bonusCampaignExcludeTypeId !== null && values.bonusCampaignExcludeTypeId > 0
  }, [values.bonusCampaignExcludeTypeId])

  useEffect(() => {
    if (isBonusCampaignExcludeTypeIdSelected) {
      setIbInputText('')
      setAffiliateInputText('')
      setFieldValue('bonusCampaignIntroducingBrokers', [])
      setFieldValue('bonusCampaignAffiliates', [])
    }
  }, [isBonusCampaignExcludeTypeIdSelected])

  const labelOptions = useMemo(
    () =>
      (bonusLabels || [])
        .filter((label) => label.isAvailableForBonusCampaigns)
        .map((label) => ({ id: label.id, name: label.name })),
    [bonusLabels]
  )

  return (
    <div className='px-4 md:px-6'>
      <Form>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              <Button disabled={isSubmitting} key='button' type='button' onClick={handleSubmitForm}>
                Submit
              </Button>
            </ButtonGroup>
          }
        >
          <DataColumn>
            <DataCard title='General Setting'>
              <FormField
                name='internalName'
                label='Internal Name'
                error={errors.internalName}
                value={values.internalName}
              />
              <FormField
                name='name'
                label='Bonus name (Client Facing)'
                error={errors.name}
                value={values.name}
              />
              <FormMultiSelectField
                name='tickmillCompanyIds'
                label='Operating Company / Entity'
                value={values.tickmillCompanyIds}
                options={tickmillCompanies}
                onChange={(value) =>
                  handleSetCompany(Array.isArray(value) ? (value as number[]) : [])
                }
              />
              <FormDateField
                name='startDate'
                label='Start date'
                showTimeSelect
                dateFormatType={dateFormatType}
                value={values.startDate ? new Date(values.startDate) : null}
              />
              <FormDateField
                name='endDate'
                label='End date'
                showTimeSelect
                dateFormatType={dateFormatType}
                value={values.endDate ? new Date(values.endDate) : null}
              />
              <FormSelectField
                name='bonusCampaignStatusId'
                label='Status'
                value={values.bonusCampaignStatusId}
                options={BonusCampaignStatus}
                onChange={(value) => setFieldValue('bonusCampaignStatusId', value ?? 0)}
              />
              <FormSelectField
                name='bonusLabelId'
                label='Label'
                value={values.bonusLabelId ?? undefined}
                options={labelOptions}
                allowClear
                error={errors.bonusLabelId}
                onChange={(value) => setFieldValue('bonusLabelId', value ?? null)}
              />
            </DataCard>

            <DataCard title='Eligibility'>
              <FormReadonlyField label='Deposit Method' value='All' />
              <FormSelectField
                name='defaultCurrencyId'
                label='Currency'
                value={values.defaultCurrencyId || undefined}
                options={walletCurrencies}
                allowClear
                onChange={(value) => setFieldValue('defaultCurrencyId', value ?? '')}
              />
              <FormCheckboxField name='noPreviousDeposits' label='No Previous Deposits (FTD)' />
              <FormField
                name='minTotalTradedLots'
                label='Minimum Total Traded Lots'
                type='number'
                value={values.minTotalTradedLots?.toString()}
              />
              <FormField
                name='maxTotalPnl'
                label='Maximum Profile Total PNL'
                type='number'
                value={values.maxTotalPnl?.toString()}
              />
              <FormCheckboxField
                name='targetOnlyInactiveClients'
                label='Target only inactive clients'
              />
              <FormSelectField
                name='bonusCampaignExcludeTypeId'
                label='Exclude'
                value={values.bonusCampaignExcludeTypeId ?? undefined}
                options={bonusCampaignExcludeTypes}
                onChange={(value) => setFieldValue('bonusCampaignExcludeTypeId', value ?? 0)}
              />
              <FormField
                name='ibSelectionText'
                label='IB Selection'
                placeholder='Enter IB codes separated by commas (e.g., IB66470039, IB99468989)'
                value={getIBDisplayValue()}
                disabled={isBonusCampaignExcludeTypeIdSelected}
                onChange={(event) => handleOnSetIBs(event.target.value)}
                onBlur={processIBsOnBlur}
              />
              <FormField
                name='affiliateSelectionText'
                label='Affiliate Selection'
                placeholder='Enter affiliate codes separated by commas (e.g., 35076, 35170)'
                value={getAffiliateDisplayValue()}
                disabled={isBonusCampaignExcludeTypeIdSelected}
                onChange={(event) => handleOnSetAffiliates(event.target.value)}
                onBlur={processAffiliatesOnBlur}
              />
              <FormField
                name='utmSelectionText'
                label='Campaign UTM'
                placeholder='Enter UTM codes separated by commas (e.g., utm_source1, utm_campaign1)'
                value={getUtmDisplayValue()}
                onChange={(event) => handleOnSetUtms(event.target.value)}
                onBlur={processUtmsOnBlur}
              />
            </DataCard>
          </DataColumn>

          <DataColumn>
            <DataCard title='Bonus Setup'>
              <FormSelectField
                name='bonusTypeId'
                label='Bonus Type'
                value={values.bonusTypeId}
                options={bonusTypes}
                error={errors.bonusTypeId}
                onChange={(value) => setFieldValue('bonusTypeId', value ?? '')}
              />
              <FormField
                name='termsAndConditionsUrl'
                label='Link to T&C'
                value={values.termsAndConditionsUrl}
                error={errors.termsAndConditionsUrl}
              />
              <FormField
                name='minDeposit'
                label='Minimum deposit'
                type='number'
                error={errors.minDeposit}
                value={values.minDeposit?.toString()}
              />
              <FormField
                name='maxBonus'
                label='Maximum bonus'
                type='number'
                value={values.maxBonus?.toString()}
              />
            </DataCard>

            <DataCard title='Lifecycle'>
              <FormDateField
                name='bonusExpirationDate'
                label='Period (Bonus expiration date)'
                dateFormatType={dateFormatType}
                value={values.bonusExpirationDate ? new Date(values.bonusExpirationDate) : null}
              />
              <FormSelectField
                name='bonusCampaignAvailabilityPeriodId'
                label='Availability (Claim) Period'
                value={values.bonusCampaignAvailabilityPeriodId ?? undefined}
                options={bonusCampaignAvialabilityPeriods}
                error={errors.bonusCampaignAvailabilityPeriodId}
                onChange={(value) => setFieldValue('bonusCampaignAvailabilityPeriodId', value ?? 0)}
              />
              <FormField
                name='value'
                label='Value'
                type='number'
                value={values.value?.toString()}
                error={errors.value}
                disabled={!isBonusCampaignAvailabilityPeriodIdSelected}
              />

              <FormSelectField
                name='bonusCampaignCountdownTriggerId'
                label='Countdown trigger'
                value={values.bonusCampaignCountdownTriggerId ?? undefined}
                options={bonusCampaignCountdownTriggers}
                allowClear
                readOnly={!isBonusCampaignAvailabilityPeriodIdSelected}
                disabled={!isBonusCampaignAvailabilityPeriodIdSelected}
                error={errors.bonusCampaignCountdownTriggerId}
                onChange={(value) =>
                  setFieldValue('bonusCampaignCountdownTriggerId', value ?? null)
                }
              />
              <FormField
                name='validDaysAfterActivation'
                label='Valid Days After Activation'
                type='number'
                error={errors.validDaysAfterActivation}
                value={values.validDaysAfterActivation?.toString()}
              />
              <FormField
                name='firstTradeRequirementFromDays'
                label='First trade requirement (days)'
                type='number'
                value={values.firstTradeRequirementFromDays?.toString()}
                placeholder='Optional - leave empty for no requirement'
              />
              <FormCheckboxField name='availableOnlyOnce' label='Available only once' />
            </DataCard>
          </DataColumn>
        </DataGrid>
      </Form>
    </div>
  )
}

interface OuterProps {
  tickmillCompanies: TickmillCompanyDto[]
  bonusTypes: NameDto[]

  bonusLabels: BonusLabel[]
  bonusCampaignExcludeTypes: BonusCampaignExcludeType[]
  bonusCampaignAvialabilityPeriods: BonusCampaignAvailabilityPeriod[]
  bonusCampaignCountdownTriggers: BonusCampaignCountdownTrigger[]

  onSubmit(values: AddCampaignDetailsFormValues): Promise<void>
}

function dataToSubmit(values: AddCampaignDetailsFormValues): AddCampaignDetailsFormValues {
  return {
    name: values.name,
    tickmillCompanyIds: values.tickmillCompanyIds || [],
    minDeposit: values.minDeposit,
    maxBonus: values.maxBonus,
    termsAndConditionsUrl: values.termsAndConditionsUrl,
    startDate: values.startDate,
    endDate: values.endDate,
    bonusExpirationDate: values.bonusExpirationDate,
    bonusTypeId: values.bonusTypeId,
    bonusPercent: values.bonusPercent || null,
    bonusAmount: values.bonusAmount || null,
    bonusCampaignStatusId: values.bonusCampaignStatusId,
    defaultCurrencyId: values.defaultCurrencyId || null,
    campaignPaymentMethods: values.campaignPaymentMethods || [],
    bonusCampaignAffiliates: values.bonusCampaignAffiliates,
    bonusCampaignUtms: values.bonusCampaignUtms,
    bonusCampaignIntroducingBrokers: values.bonusCampaignIntroducingBrokers,
    maxTotalPnl: values.maxTotalPnl,
    minTotalTradedLots: values.minTotalTradedLots,
    noPreviousDeposits: values.noPreviousDeposits,
    validDaysAfterActivation: values.validDaysAfterActivation,
    firstTradeRequirementFromDays: values.firstTradeRequirementFromDays,
    targetOnlyInactiveClients: values.targetOnlyInactiveClients,
    availableOnlyOnce: values.availableOnlyOnce,
    bonusLabelId: values.bonusLabelId,
    bonusCampaignExcludeTypeId: values.bonusCampaignExcludeTypeId,
    bonusCampaignAvailabilityPeriodId: values.bonusCampaignAvailabilityPeriodId,
    internalName: values.internalName,
    value: values.value,
    bonusCampaignCountdownTriggerId: values.bonusCampaignCountdownTriggerId,
  }
}

export const AddBonusCampaignDetailsForm = withFormik<OuterProps, AddCampaignDetailsFormValues>({
  mapPropsToValues: () => ({
    name: '',
    minDeposit: 0,
    maxBonus: 0,
    termsAndConditionsUrl: '',
    startDate: '',
    endDate: '',
    bonusExpirationDate: '',
    bonusTypeId: '',
    bonusPercent: null,
    bonusAmount: 0,
    bonusCampaignStatusId: 0,
    defaultCurrencyId: '',
    campaignPaymentMethods: [],
    tickmillCompanyIds: [],
    bonusCampaignAffiliates: [],
    bonusCampaignAudiences: [],
    bonusCampaignUtms: [],
    bonusCampaignIntroducingBrokers: [],
    maxTotalPnl: null,
    minTotalTradedLots: null,
    noPreviousDeposits: false,
    validDaysAfterActivation: null,
    firstTradeRequirementFromDays: null,
    targetOnlyInactiveClients: false,
    bonusCampaignAvailabilityPeriodId: 0,
    bonusCampaignCountdownTriggerId: null,
    bonusLabelId: null,
    bonusCampaignExcludeTypeId: 0,
    internalName: '',
    value: null,
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddCampaignDetailsFormValues> = {}
    if (!values.internalName) {
      errors.internalName = 'Internal Name is required'
    }
    if (!values.bonusLabelId) {
      errors.bonusLabelId = 'Bonus Label is required'
    }
    if (!values.name) errors.name = 'Bonus Campaign Name is required'
    if (values.minDeposit <= 0) errors.minDeposit = 'Minimum deposit must be greater than 0'
    if (!!values.termsAndConditionsUrl && !UrlPattern.test(values.termsAndConditionsUrl)) {
      errors.termsAndConditionsUrl = 'Invalid URL. Please start with https://.'
    }

    const hasAvailabilityPeriod =
      values.bonusCampaignAvailabilityPeriodId !== null &&
      values.bonusCampaignAvailabilityPeriodId > 0
    const hasValidDays = values.validDaysAfterActivation && values.validDaysAfterActivation > 0

    if (hasAvailabilityPeriod && hasValidDays) {
      errors.bonusCampaignAvailabilityPeriodId =
        'Cannot use both Available claim period and Valid days after activation'
      errors.validDaysAfterActivation =
        'Cannot use both Available claim period and Valid days after activation'
    }
    if (!values.bonusTypeId) {
      errors.bonusTypeId = 'Bonus Type is required'
    }

    if (hasAvailabilityPeriod) {
      if (values.value === null || values.value <= 0) {
        errors.value = 'Value must be greater than 0'
      }
      if (!values.bonusCampaignCountdownTriggerId || values.bonusCampaignCountdownTriggerId <= 0) {
        errors.bonusCampaignCountdownTriggerId = 'Countdown Trigger is required'
      }
    }
    return errors
  },
})(AddBonusCampaignDetailsFormUI)
